
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { Database } from '@/integrations/supabase/types';

type CustomerProfile = Database['public']['Tables']['customer_profiles']['Row'];

export const useUserManagement = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { toast } = useToast();
  const [users, setUsers] = useState<CustomerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<CustomerProfile | null>(null);
  const [pointsToAdd, setPointsToAdd] = useState('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('customer_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:errorFetchingUsers'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddPoints = async () => {
    if (!selectedUser || !pointsToAdd || isNaN(Number(pointsToAdd))) {
      toast({
        title: t('admin:invalidInput'),
        description: t('admin:enterValidPoints'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const points = parseInt(pointsToAdd);
      
      const { error } = await supabase.rpc('add_points_to_customer', {
        p_customer_id: selectedUser.id,
        p_points: points,
        p_description: 'Added by admin',
        p_source: 'admin'
      });

      if (error) throw error;

      toast({
        title: t('admin:success'),
        description: t('admin:pointsAdded', { points, user: `${selectedUser.first_name} ${selectedUser.last_name}` }),
      });

      setPointsToAdd('');
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error adding points:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:errorAddingPoints'),
        variant: 'destructive',
      });
    }
  };

  const handleUpdateTier = async (userId: string, newTier: string) => {
    try {
      const { error } = await supabase
        .from('customer_profiles')
        .update({ tier: newTier })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: t('admin:success'),
        description: t('admin:tierUpdated'),
      });

      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Error updating tier:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:errorUpdatingTier'),
        variant: 'destructive',
      });
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  return {
    users: filteredUsers,
    loading,
    searchQuery,
    setSearchQuery,
    selectedUser,
    setSelectedUser,
    pointsToAdd,
    setPointsToAdd,
    handleAddPoints,
    handleUpdateTier
  };
};
